/* import __COLOCATED_TEMPLATE__ from './trigger-examples-editor.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { A } from '@ember/array';
import QuestionTrainingState from 'embercom/models/custom-answers/ui/question-training-state';
import type IntlService from 'ember-intl/services/intl';
import {
  SHOULD_NOT_TRIGGER_EXAMPLES,
  type TriggerExampleType,
} from 'embercom/lib/operator/custom-bots/constants';
import type Workflow from 'embercom/models/operator/visual-builder/workflow';
import type CustomBotData from 'embercom/models/operator/custom-bots/custom-bot-data';

interface ExampleQuestion {
  identifier: string;
  text: string;
  addedFrom: string;
}

interface TriggerExample {
  id: string;
  languageCode: string;
  exampleQuestions: ExampleQuestion[];
  controlCriteria: {
    serialize: () => Record<string, unknown>;
  };
}

interface Args {
  customBot: $TSFixMe;
  onClose: () => void;
  exampleType: TriggerExampleType;
}

export default class TriggerExamplesEditor extends Component<Args> {
  @service declare intercomEventService: $TSFixMe;
  @service declare appService: AppService;
  @service declare intl: IntlService;

  @tracked questionTrainingState: any;
  @tracked addManualExampleModalIsVisible = false;
  @tracked manualExample = '';
  @tracked hasSearched = false;
  @tracked exampleQuestions = A<ExampleQuestion>([]);

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this._loadExistingExamples();
    this._initQuestionTrainingState();
  }

  _loadExistingExamples(): void {
    if (this.botData?.[this.exampleType]) {
      try {
        let existingExamples = JSON.parse(this.botData[this.exampleType]);

        if (Array.isArray(existingExamples)) {
          this.exampleQuestions = A(
            existingExamples.map((example: any) => ({
              identifier: example.identifier,
              text: example.text,
              addedFrom: example.addedFrom,
            })),
          );
        }
      } catch (error) {
        console.warn(`Failed to parse existing ${this.exampleType} trigger examples:`, error);
        this.exampleQuestions = A([]);
      }
    }
  }

  _saveExamplesToCustomBot(): void {
    if (this.botData) {
      let examples = this.exampleQuestions.map((example) => ({
        identifier: example.identifier,
        text: example.text,
        addedFrom: example.addedFrom,
      }));
      this.botData[this.args.exampleType] = JSON.stringify(examples);
    }
  }

  get botData(): CustomBotData {
    if (this.appService.app.canUseFeature?.('workflow-draft-state')) {
      return this.workflow.additionalData.customBotData;
    }
    return this.args.customBot.botData;
  }

  // A simple custom answer like object that allows us to re-use the custom answer training components
  get matchExample(): TriggerExample {
    return {
      id: 'matching-example',
      languageCode: 'en',
      exampleQuestions: this.exampleQuestions,
      controlCriteria: {
        serialize: () => ({}),
      },
    };
  }

  _initQuestionTrainingState(): void {
    this.questionTrainingState = QuestionTrainingState.create({
      answer: this.matchExample,
      isNewFromSuggestion: false,
      isNewFromCluster: false,
    });
  }

  get shouldShowProgress(): boolean {
    return this.hasSearched || this.exampleQuestions.length > 0;
  }

  get exampleType(): TriggerExampleType {
    return this.args.exampleType;
  }

  get isNegativeExamples(): boolean {
    return this.exampleType === SHOULD_NOT_TRIGGER_EXAMPLES;
  }

  get headerDescription(): string {
    return this.isNegativeExamples
      ? this.intl.t(
          'operator.workflows.visual-builder.workflow-trigger-node.fin-task.should-not-trigger-examples-description',
        )
      : this.intl.t(
          'operator.workflows.visual-builder.workflow-trigger-node.fin-task.should-trigger-examples-description',
        );
  }

  @action
  showAddManualExampleModal(visible: boolean): void {
    this.addManualExampleModalIsVisible = visible;
  }

  @action
  addManualExample(text: string): void {
    text = text.trim();

    if (text.length > 0) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'added',
        object: 'manual_trigger_example',
        context: 'trigger_examples_editor',
        example_type: this.args.exampleType,
        models: [this.args.customBot],
      });

      let newExample: ExampleQuestion = {
        identifier: `manual-${Date.now()}`,
        text,
        addedFrom: 'manual',
      };

      this.exampleQuestions.pushObject(newExample);
      this.questionTrainingState.addRecentExample({ text }, 'manual');
      this._saveExamplesToCustomBot();
    }

    this.addManualExampleModalIsVisible = false;
    this.manualExample = '';
  }

  @action
  onNewSearchResults(): void {
    this.hasSearched = true;
  }

  @action
  acceptSuggestedQuestion(searchResult: any): void {
    let newExample: ExampleQuestion = {
      identifier: searchResult.identifier || `search-${Date.now()}`,
      text: searchResult.text,
      addedFrom: 'suggestion',
    };

    this.exampleQuestions.pushObject(newExample);
    this.questionTrainingState.addRecentExample(searchResult);
    this._saveExamplesToCustomBot();
  }

  @action
  rejectSuggestedQuestion(searchResult: any): void {
    let example = this.exampleQuestions.findBy('text', searchResult.text);

    if (example) {
      this.questionTrainingState.removeExampleQuestion(example);
      this.exampleQuestions.removeObject(example);
      this._saveExamplesToCustomBot();
    }

    this.questionTrainingState.rejectExampleQuestion(searchResult);
  }

  @action
  removeExample(exampleQuestion: ExampleQuestion): void {
    this.exampleQuestions.removeObject(exampleQuestion);
    this.questionTrainingState.removeExampleQuestion(exampleQuestion);
    this._saveExamplesToCustomBot();
  }

  get workflow(): Workflow {
    return this.args.customBot.activeWorkflow;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::TriggerExamplesEditor': typeof TriggerExamplesEditor;
    'workflows/graph-editor/trigger-examples-editor': typeof TriggerExamplesEditor;
  }
}
